import { firstValueFrom } from 'rxjs';
import last from 'lodash/last';
import {
  Component,
  ComponentFactoryResolver, OnChanges,
  signal,
  OnInit, SimpleChanges
} from '@angular/core';
import { DeliveryCalendar } from '@cakeiteasy/web-ui';
import {
  POST_NUMBER_LENGTHS,
  CieUtilsModalManagerService, CalendarSchedule, IDropdownItem, ToDoorCalendarDay, AddressLocationManager, ngHelper, UiContextService
} from '@cakeiteasy/web-utils';
import moment from 'moment';
import { CitiesApiService, ICitySearch } from '../../../api-services/services/cities-api.service';
import { DeliveryAvailabilityService } from '../../services/delivery-availability.service';
import { DeliveryUiHelperService } from '../../services/delivery-ui-helper.service';
import { ChooseDeliveryAddressModalComponent } from '../choose-delivery-address-modal/choose-delivery-address-modal.component';
import { IDeliveryAddress } from '../../../users';
import { DeliveryApiService } from '../../../api-services/services/delivery-api.service';
import { CART_DELIVERY_TYPES, DELIVERY_ORDER_DELIVERY_TYPES, ICustomDeliveryTime } from '../../../cart';
import { FormBuilder, Validators } from '@angular/forms';
import { getDeliveryFormGroup } from '../../delivery-helper';
import { filter, tap } from 'rxjs/operators';
import { DeliveryFormBase } from '../../../base-classes/delivery-form.base';

@Component({
  standalone: false,
  selector: 'delivery-to-door-old',
  templateUrl: './delivery-to-door-old.component.html',
  styleUrls: ['./delivery-to-door-old.component.scss']
})
export class DeliveryToDoorOldComponent extends DeliveryFormBase implements OnInit, OnChanges {
  reserveCityName = '';
  canContinueDelivery = signal(true);
  deliveryPrices = signal<any[] | null>(null);
  deliveryScheduleOverrides: ICustomDeliveryTime[];
  postNumBusy: Promise<any>;
  calendar = signal<DeliveryCalendar | null>(null);
  isCalendarLoading = false;
  todayStr = moment().format('YYYY-MM-DD');
  skeletonPromise: Promise<any>;

  get isCityValid() {
    return this.deliveryForm && this.deliveryForm.get('deliveryPostNumber').valid && this.deliveryForm.get('deliveryCity').valid;
  }

  constructor(protected deliveryUi: DeliveryUiHelperService,
              protected modalManager: CieUtilsModalManagerService,
              protected componentResolver: ComponentFactoryResolver,
              private addressManager: AddressLocationManager,
              private citiesApi: CitiesApiService,
              private deliveryAvailability: DeliveryAvailabilityService,
              private deliveryApiService: DeliveryApiService,
              private _uiContext: UiContextService,
              private formBuilder: FormBuilder) {
    super();

    this.deliveryForm = this.getDeliveryForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    ngHelper.ifChanged(changes, 'deliveryAddresses', (deliveryAddresses: IDeliveryAddress[]) => {
      this.initByFavoriteAddress(deliveryAddresses);
    });
  }

  async ngOnInit(): Promise<void> {
    const initialValue = this.getInitialFormValue(CART_DELIVERY_TYPES.DELIVERY);
    if (initialValue) {
      this.patchDeliveryForm(initialValue);
    }

    this._subscribeOnDeliveryDate();
    this.subscribeOnPostNumber();
    this._watchDraft();

    if (this.isCityValid) {
      const dateValue = initialValue?.deliveryDate || this.todayStr;
      await (this.skeletonPromise = this.loadCalendar(dateValue));

      const calendarDay = this.calendar().getDate(dateValue);
      this.scheduleChange(calendarDay);
    }
  }

  private async initByFavoriteAddress(deliveryAddresses: IDeliveryAddress[]): Promise<void> {
    const address = this.getInitialAddress(deliveryAddresses);

    if (!address) {
      return;
    }

    this.patchByDeliveryAddress(address);
  }

  findSchedule(schedule: CalendarSchedule[], timeValue: string): CalendarSchedule {
    return schedule.find(({ timeRange }) => timeRange === timeValue);
  }

  contextInit(context) {
    this.dp = context;

    this._tryToDatepickerScroll();
  }

  setPrice(price?: number) {
    if (this.overwritingDelivery) {
      this.price = this.overwritingDelivery.price || 0;
    } else {
      this.price = price || 0;
    }

    this.isPriceSet = true;
  }

  async receiveScheduleFromDay(calendarDay: ToDoorCalendarDay): Promise<CalendarSchedule[]> {
    const { schedule } = last(calendarDay.variants);
    return schedule;
  }

  mapScheduleToTimeItems(schedule: CalendarSchedule[]): IDropdownItem[] {
    return schedule
      .map((item: CalendarSchedule) => ({ id: item.timeRange, label: item.timeRange, ref: item }));
  }

  findDeliveryLocation(postNumber: string): Promise<ICitySearch | null> {
    return this.citiesApi.searchLocationByPostNumber(this.countryCode, postNumber);
  }

  calendarFetcher(fromDate: Date, prevCalendar?: DeliveryCalendar, monthCount = 2) {
    const postNumber = this.deliveryForm.get('deliveryPostNumber').value;

    return this.getCalendarByPostNum({ fromDate, postNumber, prevCalendar, monthCount });
  }

  async onMonthNavigate(monthDate: string) {
    await this.loadCalendar(monthDate, this.calendar());
  }

  timeChanged({ ref }: IDropdownItem) {
    this.setPrice(ref.price);
  }

  async deliveryCityChecker({ postNumber }: IDeliveryAddress): Promise<boolean> {
    const city = await this.findDeliveryLocation(postNumber);
    return Boolean(city) && (await this.checkDeliveryContinuable(postNumber));
  }

  async chooseDeliveryAddress(): Promise<void> {
    const data = {
      deliveryAddresses: this.deliveryAddresses,
      deliveryChecker: this.deliveryCityChecker.bind(this)
    };

    try {
      const deliveryAddress: IDeliveryAddress = await this.modalManager.show(
        ChooseDeliveryAddressModalComponent,
        this.componentResolver, data, { windowClass: 'modal-simple lg' }
      );

      this.patchByDeliveryAddress(deliveryAddress);
    } catch (e) {
      console.log('User chosen nothing');
    }
  }

  async confirmDelivery() {
    await (this.busy = this.patchAddressCoordinates());

    const { COURIER: deliveryType } = DELIVERY_ORDER_DELIVERY_TYPES;
    const { DELIVERY: type } = CART_DELIVERY_TYPES;

    this.onNext.emit({
      deliveryOrder: {
        ...this.deliveryOrder,
        ...this.deliveryForm.getRawValue(),
        outlet: null,
        externalDeliveryFee: 0,
        deliveryType, type
      },
      saveCurrentDelivery: this.saveAddressOnSubmit
    });
  }

  private getDeliveryForm() {
    const postNumValidators = [
      Validators.required,
      Validators.minLength(POST_NUMBER_LENGTHS[this.countryCode])
    ];

    return this.formBuilder.group({
      ...getDeliveryFormGroup(),
      deliveryLatitude: [''],
      deliveryLongitude: [''],
      deliveryPostNumber: ['', postNumValidators]
    });
  }

  private subscribeOnPostNumber() {
    const postNumCtrl = this.deliveryForm.get('deliveryPostNumber');
    const cityCtrl = this.deliveryForm.get('deliveryCity');

    postNumCtrl.valueChanges
      .pipe(tap(() => {
        this.canContinueDelivery.set(true);
        this.deliveryPrices.set(null);
        this.reserveCityName = '';
        this.calendar.set(null);
        cityCtrl.patchValue(null);
      }))
      .pipe(filter(() => postNumCtrl.valid))
      .subscribe(async (postNum) => {
        const citySearch = await (this.postNumBusy = this.findDeliveryLocation(postNum));

        if (!citySearch) {
          this.canContinueDelivery.set(false);
          return;
        }

        this.reserveCityName = citySearch.placeName || citySearch.cityName;
        this.canContinueDelivery.set(await this.checkDeliveryContinuable(postNum));

        const city = this.canContinueDelivery() ? (citySearch.placeName || citySearch.cityName) : null;
        cityCtrl.patchValue(city);

        const dateValue = this.deliveryForm.get('deliveryDate').value || this.todayStr;

        if (this.canContinueDelivery()) {
          await Promise.all([
            this.loadCalendar(dateValue),
            this.loadDeliveryPrice(postNum),
            this.loadCustomDeliveryTimes(postNum),
          ]);
        }
      });
  }

  private getCalendarByPostNum(params: { fromDate: Date, postNumber: string, prevCalendar?: DeliveryCalendar, monthCount: number }) {
    const { postNumber, monthCount, fromDate, prevCalendar } = params;
    return this.orderId
      ? this.deliveryAvailability.getOrderDeliveryCalendar(this.orderId, postNumber, monthCount, fromDate, prevCalendar)
      : this.deliveryAvailability.getDeliveryCalendar(this.bakeryId, postNumber, monthCount, fromDate, prevCalendar);
  }

  private async loadDeliveryPrice(postNumber: string): Promise<void> {
    if (!this.showDeliveryPricesInfo) {
      return;
    }

    try {
      this.deliveryPrices.set(await firstValueFrom(this.deliveryApiService
        .getBakeryDeliveryPrice(this.bakeryId, postNumber)));
    } catch (e) {
    }
  }

  private async loadCustomDeliveryTimes(postNumber: string) {
    if (!this.showDeliveryPricesInfo) {
      return;
    }

    try {
      this.deliveryScheduleOverrides = await firstValueFrom(this.deliveryApiService
        .getDeliveryScheduleOverrides(this.bakeryId, postNumber));
    } catch (error) {
      console.error(error);
    }
  }

  private patchByDeliveryAddress({
                                   postNumber: deliveryPostNumber, recipient: deliveryUserName,
                                   address: deliveryAddress, phone: deliveryPhone, note: deliveryNote,
                                 }: IDeliveryAddress) {
    this.deliveryForm.patchValue({
      deliveryPostNumber,
      deliveryUserName,
      deliveryAddress,
      deliveryPhone,
      ...(deliveryNote && { deliveryNote }),
    });
  }

  private async patchAddressCoordinates(): Promise<void> {
    const { deliveryAddress, deliveryPostNumber, deliveryCity } = this.deliveryForm.getRawValue();
    const address = `${deliveryAddress}, ${deliveryPostNumber} ${deliveryCity}`;
    const { location: { lat, lng } } = await firstValueFrom(this.addressManager.getLocationInfo(address, this.countryCode));
    this.deliveryForm.patchValue({ deliveryLatitude: lat, deliveryLongitude: lng });
  }

  private async checkDeliveryContinuable(postNumber: string): Promise<boolean> {
    try {
      const params = {
        fromDate: new Date(), postNumber, prevCalendar: null, monthCount: 1
      };
      return Boolean(await this.getCalendarByPostNum(params));
    } catch (e) {
      return false;
    }
  }

  private async loadCalendar(monthDate: string, prevCalendar?: DeliveryCalendar): Promise<void> {
    const fromDate = moment(monthDate, 'YYYY-MM-DD', true).toDate();
    this.isCalendarLoading = true;

    try {
      this.calendar.set(await this.calendarFetcher(fromDate, prevCalendar));
    } finally {
      this.isCalendarLoading = false;
    }
  }

  private _subscribeOnDeliveryDate() {
    this.addSubscriber(this.deliveryForm.get('deliveryDate').valueChanges
      .subscribe((dateValue: string) => this._handleDeliveryDateChange(dateValue)));
  }

  private async _handleDeliveryDateChange(dateValue: string) {
    if (!dateValue || !this.calendar() || this.calendar().isDateInactive(dateValue)) {
      return;
    }

    const calendarDay = this.calendar().getDate(dateValue);
    if (calendarDay) {
      await this.scheduleChange(calendarDay as ToDoorCalendarDay);
    }
  }

  private _tryToDatepickerScroll() {
    setTimeout(() => {
      try {
        if (this.deliveryForm.get('deliveryDate').valid || !this.dp || !this.datePickerLabel
          || !this.datePickerLabel.nativeElement || this._uiContext.isMobile()) {
          return;
        }

        this.dp.open();
        this.deliveryUi.scrollToDatepicker(this.datePickerLabel, 410);
      } catch (e) {
        console.error(e);
      }
    }, 700);
  }
}
